import { useEffect, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useCart } from '../lib/cart-store.js';
import { useAuth } from '../lib/auth-store.js';
import { useFavorites } from '../lib/favorites-store.js';
import { useRecent } from '../lib/recent-store.js';
import { toast } from '../lib/toast-store.js';
import { money2 } from '../lib/format.js';
import { MarginDial } from '../components/margin-dial.js';
import { QueryError } from '../components/query-error.js';

export function InventoryDetailScreen() {
  const { id } = useParams({ from: '/inventory/$id' });
  const spireId = Number(id);
  const add = useCart((s) => s.add);
  const orderCustomer = useCart((s) => s.selectedCustomer);
  // Per-user permission: without it, every line is Spire-priced (no dial, no
  // ladder picks) - enforced server-side too.
  const canOverride = useAuth((st) => st.user?.canOverridePrice ?? true);
  const favHas = useFavorites((s) => s.has);
  const favToggle = useFavorites((s) => s.toggle);

  const [qty, setQty] = useState(1);
  // Pricing selection: UOM + price level. null = "use the item's defaults".
  const [uomCode, setUomCode] = useState<string | null>(null);
  const [level, setLevel] = useState<number | null>(null);
  // Manual override from the margin dial — wins over the selected level.
  const [manualPrice, setManualPrice] = useState<string | null>(null);
  const [showDial, setShowDial] = useState(false);
  const [added, setAdded] = useState(false);

  const item = useQuery({
    queryKey: ['inventory', 'detail', spireId],
    queryFn: ({ signal }) => api.inventory.getById(spireId, signal),
  });

  const data = item.data;

  // ── Pricing model ──────────────────────────────────────────────────────
  const sellUoms = (data?.unitOfMeasures ?? []).filter((u) => u.sellUom);
  const uomList = sellUoms.length ? sellUoms : (data?.unitOfMeasures ?? []);
  const activeUom = uomCode ?? data?.uom ?? uomList[0]?.code ?? null;
  const activeUomDef = uomList.find((u) => u.code === activeUom) ?? null;

  // 20-element ladder for the active UOM (falls back to the flat sellPrices).
  const ladder = (activeUom && data?.pricingByUom?.[activeUom]) || data?.sellPrices || [];
  const pricedLevels = ladder
    .map((price, i) => ({ level: i + 1, price }))
    .filter((x) => Number(x.price) > 0);

  // Customer-specific estimate (contract/matrix → level → default). Display
  // only — untouched lines post without a price and Spire prices them.
  const quote = useQuery({
    queryKey: ['price-quote', spireId, orderCustomer?.customerNo, activeUom, qty],
    queryFn: ({ signal }) =>
      api.inventory.priceQuote(
        spireId,
        { customerNo: orderCustomer!.customerNo, uom: activeUom ?? undefined, qty: String(qty) },
        signal,
      ),
    enabled: orderCustomer != null && data != null,
    staleTime: 60_000,
  });
  const contractQuote = quote.data?.source === 'contract' ? quote.data : null;

  // Default to the bound customer's price tier when building an order for them.
  const selectedLevel = level ?? orderCustomer?.sellLevel ?? pricedLevels[0]?.level ?? 1;
  const basePrice =
    pricedLevels.find((x) => x.level === selectedLevel)?.price ?? data?.defaultSellPrice ?? '0';
  // Explicit picks win: margin-dial price, then a hand-picked level. Otherwise
  // show the customer quote when we have one, falling back to the ladder.
  // A zero quote means the chosen UOM simply has no pricing — don't show $0.00.
  const quotedPrice =
    level === null && quote.data && Number(quote.data.unitPrice) > 0
      ? quote.data.unitPrice
      : null;
  const price = manualPrice ?? quotedPrice ?? basePrice;
  // Only an explicit action (dial or level pick) overrides Spire's pricing.
  const priceOverridden = manualPrice !== null || level !== null;

  const canMargin = canOverride && data?.currentCost !== undefined && Number(data.currentCost) > 0;
  const avail = Number(data?.availableQty ?? 0);

  // Inbound purchase orders — only fetched when the item shows no stock, so
  // "out of stock" becomes "on order, expected ~date" instead of a dead end.
  const inbound = useQuery({
    queryKey: ['inventory', 'inbound', spireId],
    queryFn: ({ signal }) => api.inventory.inbound(spireId, signal),
    enabled: data != null && avail <= 0,
    staleTime: 5 * 60_000,
  });
  const factor = Number(activeUomDef?.quantityFactor ?? 1);

  const pushItem = useRecent((s) => s.pushItem);
  useEffect(() => {
    if (data) pushItem({ spireId: data.spireId, partNo: data.partNo, description: data.description });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.spireId]);

  function pickUom(code: string) {
    setUomCode(code);
    setLevel(null); // re-default the level within the new UOM's ladder
    setManualPrice(null);
  }
  function pickLevel(n: number) {
    if (!canOverride) return; // ladder stays informative, not interactive
    setLevel(n);
    setManualPrice(null);
  }

  return (
    <>
      <div className="topbar">
        <Link to="/inventory" className="back-btn">
          ‹ Inventory
        </Link>
      </div>

      {orderCustomer && (
        <div className="order-ctx">
          Ordering for <strong>{orderCustomer.name}</strong>
          {orderCustomer.sellLevel > 1 ? ` · price level ${orderCustomer.sellLevel}` : ''}
        </div>
      )}

      {item.isPending && <div className="kd-skeleton" style={{ height: 180, margin: 14 }} />}
      {item.isError && <QueryError message={item.error.message} onRetry={() => void item.refetch()} />}

      {data && (
        <>
          <div className="item-image">{data.partNo}</div>

          <div className="item-meta">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <h3 style={{ margin: 0 }}>{data.description ?? data.partNo}</h3>
              <button
                type="button"
                className={`fav-btn${favHas(data.spireId) ? ' on' : ''}`}
                aria-pressed={favHas(data.spireId)}
                aria-label={favHas(data.spireId) ? 'Remove from favorites' : 'Add to favorites'}
                onClick={() =>
                  favToggle({
                    spireId: data.spireId,
                    partNo: data.partNo,
                    description: data.description,
                    whseCode: data.whseCode,
                    price: data.defaultSellPrice,
                    uom: data.uom,
                  })
                }
              >
                {favHas(data.spireId) ? '★' : '☆'}
              </button>
            </div>
            <div className="sku">SKU {data.partNo}{activeUom ? ` · ${activeUom}` : ''}</div>
          </div>

          {/* Pricing — selected price, UOM selector, and the price-level ladder */}
          <div className="block">
            <div className="price-block" style={{ padding: 0, border: 'none' }}>
              <div>
                <div className="price-cap">Price</div>
                <div className="price">{money2(price)}</div>
                <div className="level">
                  {activeUom ? `per ${activeUom}` : ''}
                  {manualPrice
                    ? ' · custom'
                    : contractQuote && level === null
                      ? ` · contract${contractQuote.promoCode ? ` (${contractQuote.promoCode})` : ''}`
                      : pricedLevels.length > 1
                        ? ` · level ${selectedLevel}`
                        : ''}
                </div>
                {!manualPrice && level === null && quote.data?.serverPriced && (
                  <div className="kd-muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                    Contract pricing applies — final price set by Spire at order entry.
                  </div>
                )}
                {(quote.data?.qtyBreaks.length ?? 0) > 0 && (
                  <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {quote.data!.qtyBreaks.map((b) => {
                      const active = qty >= Number(b.minQty);
                      return (
                        <button
                          key={b.minQty}
                          type="button"
                          className={`chip${active ? ' active' : ''}`}
                          style={{ fontSize: 11.5 }}
                          onClick={() => setQty(Number(b.minQty))}
                          title={`Set quantity to ${b.minQty}`}
                        >
                          {b.minQty}+ · {money2(b.unitPrice)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {uomList.length > 1 && (
              <>
                <div className="pricing-label">Unit of measure</div>
                <div className="filter-chips" style={{ padding: '0 0 4px' }}>
                  {uomList.map((u) => (
                    <button
                      key={u.code}
                      type="button"
                      className={`chip${u.code === activeUom ? ' active' : ''}`}
                      onClick={() => pickUom(u.code)}
                    >
                      {u.code}
                    </button>
                  ))}
                </div>
                {factor !== 1 && (
                  <div className="kd-muted" style={{ fontSize: 11.5 }}>
                    1 {activeUom} = {factor} base units
                  </div>
                )}
              </>
            )}

            {pricedLevels.length > 1 && (
              <>
                <div className="pricing-label">Price level</div>
                <div className="price-ladder">
                  {pricedLevels.map(({ level: lv, price: p }) => (
                    <button
                      key={lv}
                      type="button"
                      className={`ladder-row${lv === selectedLevel && !manualPrice ? ' active' : ''}`}
                      onClick={() => pickLevel(lv)}
                    >
                      <span className="lv">Level {lv}</span>
                      <span className="pr">{money2(p)}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="block">
            <h4>Stock</h4>
            <div className="wh-row">
              <span className="wh-name">Warehouse {data.whseCode}</span>
              <span className={`wh-qty ${avail > 0 ? 'in' : 'out'}`}>
                {avail > 0 ? `${data.availableQty} available` : 'Out of stock'}
              </span>
            </div>
            <div className="wh-row">
              <span className="wh-name">On hand · committed</span>
              <span className="wh-qty">
                {data.onHandQty} · {data.committedQty}
              </span>
            </div>
            {avail <= 0 && (inbound.data?.length ?? 0) > 0 && (
              <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: '#e8f1fb', fontSize: 12.5 }}>
                <b style={{ color: 'var(--kd-navy)' }}>On order:</b>{' '}
                {inbound.data!.map((l, i) => (
                  <span key={i}>
                    {i > 0 && ' · '}
                    {l.remainingQty} expected{l.expectedDate ? ` ~${l.expectedDate}` : ''}
                    {l.vendorName ? ` (${l.vendorName})` : ''}
                  </span>
                ))}
              </div>
            )}
            {avail <= 0 && inbound.isSuccess && inbound.data.length === 0 && (
              <div className="kd-muted" style={{ marginTop: 6, fontSize: 11.5 }}>
                Nothing inbound on open purchase orders.
              </div>
            )}
          </div>

          <div className="block" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h4 style={{ margin: 0 }}>Quantity</h4>
            <div className="qty-stepper">
              <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))}>−</button>
              <span className="val">{qty}</span>
              <button type="button" onClick={() => setQty((q) => q + 1)}>+</button>
            </div>
          </div>

          {showDial && canMargin && <MarginDial cost={data.currentCost!} onPrice={setManualPrice} />}

          <div className="footer-actions">
            {canMargin && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowDial((v) => !v)}
              >
                {showDial ? 'Hide dial' : 'Margin tool'}
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                add({
                  partNo: data.partNo,
                  whseCode: data.whseCode,
                  description: data.description,
                  unitPrice: price,
                  sellMeasure: activeUom ?? undefined,
                  unitCost: data.currentCost,
                  qty,
                  // Untouched price → Spire's engine prices the line at submit.
                  priceOverridden,
                  priceSource: manualPrice
                    ? 'manual'
                    : level !== null
                      ? 'level'
                      : (quote.data?.source ?? 'default'),
                });
                setAdded(true);
                toast(`Added ${qty}× ${data.partNo}${activeUom ? ` (${activeUom})` : ''}`);
                window.setTimeout(() => setAdded(false), 1400);
              }}
            >
              {added ? 'Added ✓' : `Add ${qty} · ${money2(price)}`}
            </button>
          </div>
        </>
      )}
    </>
  );
}
